import React, { useState, useRef, createElement } from 'react';
import { XIcon, MailIcon, SmartphoneIcon, LinkIcon, CheckIcon, UserIcon, EyeIcon, EyeOffIcon, SendIcon, DollarSignIcon, QrCodeIcon, DownloadIcon } from 'lucide-react';
import { Recipient } from './InstallmentPayment';
import { QRCodeSVG } from 'qrcode.react';
interface ShareLinkModalProps {
  onClose: () => void;
  recipients?: Recipient[];
}
export function ShareLinkModal({
  onClose,
  recipients = []
}: ShareLinkModalProps) {
  const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | 'copy' | 'qrcode' | null>(null);
  const [recipient, setRecipient] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  // For multiple recipients
  const [recipientContactMethods, setRecipientContactMethods] = useState<Record<number, 'email' | 'sms' | 'both'>>({});
  // Track which recipient's link is being copied
  const [copiedRecipientId, setCopiedRecipientId] = useState<number | null>(null);
  // Track whether each recipient can see total amount and outstanding balance (per recipient)
  const [recipientsViewTotalSettings, setRecipientsViewTotalSettings] = useState<Record<number, boolean>>(() => {
    // Initialize with default (false) for each recipient
    const initialSettings: Record<number, boolean> = {};
    recipients.forEach(recipient => {
      initialSettings[recipient.id] = false;
    });
    return initialSettings;
  });
  // Check if we have multiple recipients
  const hasMultipleRecipients = recipients.length > 0;
  const handleCopyLink = (recipientId?: number) => {
    // In a real app, this would copy the actual payment link
    const linkToCopy = recipientId ? `https://payment.example.com/pay/${recipientId}-abc123${recipientsViewTotalSettings[recipientId] ? '?showTotal=true' : ''}` : 'https://payment.example.com/pay/abc123';
    navigator.clipboard.writeText(linkToCopy);
    if (recipientId) {
      setCopiedRecipientId(recipientId);
      setTimeout(() => setCopiedRecipientId(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    // Create a temporary link element
    const link = document.createElement('a');
    link.download = 'payment-qrcode.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setQrDownloaded(true);
    setTimeout(() => setQrDownloaded(false), 2000);
  };
  const handleSend = () => {
    // In a real app, this would send the email or SMS
    // For now, just close the modal
    onClose();
  };
  const getContactMethod = (recipient: Recipient): 'email' | 'sms' | 'both' => {
    // If already set, return that
    if (recipientContactMethods[recipient.id]) {
      return recipientContactMethods[recipient.id];
    }
    // Otherwise determine default based on available contact info
    if (recipient.email && recipient.phone) {
      return 'both';
    } else if (recipient.email) {
      return 'email';
    } else if (recipient.phone) {
      return 'sms';
    }
    return 'email'; // Fallback
  };
  const setContactMethod = (recipientId: number, method: 'email' | 'sms' | 'both') => {
    setRecipientContactMethods(prev => ({
      ...prev,
      [recipientId]: method
    }));
  };
  const toggleRecipientTotalView = (recipientId: number) => {
    setRecipientsViewTotalSettings(prev => ({
      ...prev,
      [recipientId]: !prev[recipientId]
    }));
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1907">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4" data-id="element-1908">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1909">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-1910">
            Share payment link
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-1911">
            <XIcon size={20} data-id="element-1912" />
          </button>
        </div>
        <div className="p-6 space-y-4" data-id="element-1913">
          {hasMultipleRecipients ? <>
              <div className="flex justify-between items-center mb-4" data-id="element-1914">
                <p className="text-gray-600 font-medium" data-id="element-1915">
                  Send unique payment links to each recipient
                </p>
              </div>
              <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-id="element-1916">
                {recipients.map((recipient, index) => <div key={recipient.id} className="border rounded-lg p-4" data-id="element-1917">
                    <div className="flex items-center mb-3" data-id="element-1918">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-1919">
                        <UserIcon size={20} className="text-blue-600" data-id="element-1920" />
                      </div>
                      <div className="ml-3" data-id="element-1921">
                        <h4 className="font-medium text-gray-800" data-id="element-1922">
                          Recipient {index + 1}
                        </h4>
                        <p className="text-sm text-gray-500" data-id="element-1923">
                          {recipient.email || recipient.phone}
                        </p>
                      </div>
                    </div>
                    {/* Toggle for this specific recipient */}
                    <div className="mb-3" data-id="element-1924">
                      <label className="flex items-center cursor-pointer" data-id="element-1925">
                        <input type="checkbox" className="sr-only" checked={recipientsViewTotalSettings[recipient.id] || false} onChange={() => toggleRecipientTotalView(recipient.id)} data-id="element-1926" />
                        <div className={`w-10 h-5 rounded-full transition-colors ${recipientsViewTotalSettings[recipient.id] ? 'bg-blue-500' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-1927">
                          <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${recipientsViewTotalSettings[recipient.id] ? 'translate-x-5' : ''}`} data-id="element-1928" />
                        </div>
                        <span className="ml-2 text-sm text-gray-600" data-id="element-1929">
                          {recipientsViewTotalSettings[recipient.id] ? <span className="flex items-center" data-id="element-1930">
                              <DollarSignIcon size={14} className="mr-1" data-id="element-1931" />
                              Allow recipient to view the total and outstanding
                              amount of the full payment
                            </span> : <span className="flex items-center" data-id="element-1932">
                              <EyeOffIcon size={14} className="mr-1" data-id="element-1933" />
                              Recipient will only see their portion
                            </span>}
                        </span>
                      </label>
                    </div>
                    {/* Payment link for this recipient */}
                    <div className="mb-3" data-id="element-1934">
                      <div className="flex" data-id="element-1935">
                        <input type="text" className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-sm" value={`https://payment.example.com/pay/${recipient.id}-abc123${recipientsViewTotalSettings[recipient.id] ? '?showTotal=true' : ''}`} readOnly data-id="element-1936" />
                        <button className="px-3 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 transition-colors flex items-center text-sm" onClick={() => handleCopyLink(recipient.id)} data-id="element-1937">
                          {copiedRecipientId === recipient.id ? <>
                              <CheckIcon size={14} className="mr-1" data-id="element-1938" />
                              Copied
                            </> : <>
                              <LinkIcon size={14} className="mr-1" data-id="element-1939" />
                              Copy
                            </>}
                        </button>
                      </div>
                    </div>
                    {/* Contact options - only show if recipient has contact info */}
                    {(recipient.email || recipient.phone) && <div className="mt-3" data-id="element-1940">
                        <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-1941">
                          Send payment link via:
                        </h5>
                        <div className="flex space-x-2" data-id="element-1942">
                          {/* Both email and phone available */}
                          {recipient.email && recipient.phone && <>
                              <button className={`flex-1 py-2 px-3 rounded-md flex items-center justify-center text-sm ${getContactMethod(recipient) === 'email' ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'email')} data-id="element-1943">
                                <MailIcon size={14} className="mr-1.5" data-id="element-1944" />
                                Email
                              </button>
                              <button className={`flex-1 py-2 px-3 rounded-md flex items-center justify-center text-sm ${getContactMethod(recipient) === 'sms' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'sms')} data-id="element-1945">
                                <SmartphoneIcon size={14} className="mr-1.5" data-id="element-1946" />
                                SMS
                              </button>
                              <button className={`flex-1 py-2 px-3 rounded-md flex items-center justify-center text-sm ${getContactMethod(recipient) === 'both' ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'both')} data-id="element-1947">
                                Both
                              </button>
                            </>}
                          {/* Only email available */}
                          {recipient.email && !recipient.phone && <button className="flex-1 py-2 px-3 rounded-md flex items-center justify-center text-sm bg-blue-100 text-blue-700 border border-blue-300 cursor-default" data-id="element-1948">
                              <MailIcon size={14} className="mr-1.5" data-id="element-1949" />
                              Email only
                            </button>}
                          {/* Only phone available */}
                          {!recipient.email && recipient.phone && <button className="flex-1 py-2 px-3 rounded-md flex items-center justify-center text-sm bg-green-100 text-green-700 border border-green-300 cursor-default" data-id="element-1950">
                              <SmartphoneIcon size={14} className="mr-1.5" data-id="element-1951" />
                              SMS only
                            </button>}
                        </div>
                      </div>}
                    {/* Display contact info */}
                    <div className="mt-3 text-sm space-y-1" data-id="element-1952">
                      {(getContactMethod(recipient) === 'email' || getContactMethod(recipient) === 'both') && recipient.email && <div className="flex items-center text-gray-600" data-id="element-1953">
                            <MailIcon size={14} className="mr-1.5" data-id="element-1954" />
                            {recipient.email}
                          </div>}
                      {(getContactMethod(recipient) === 'sms' || getContactMethod(recipient) === 'both') && recipient.phone && <div className="flex items-center text-gray-600" data-id="element-1955">
                            <SmartphoneIcon size={14} className="mr-1.5" data-id="element-1956" />
                            {recipient.phone}
                          </div>}
                    </div>
                  </div>)}
              </div>
            </> : <>
              <p className="text-gray-600 mb-4" data-id="element-1957">
                Choose how you want to share this payment link:
              </p>
              <div className="space-y-3" data-id="element-1958">
                {/* Email option */}
                <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedOption === 'email' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedOption('email')} data-id="element-1959">
                  <div className="flex items-center" data-id="element-1960">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-1961">
                      <MailIcon size={20} className="text-blue-600" data-id="element-1962" />
                    </div>
                    <div className="ml-4 flex-1" data-id="element-1963">
                      <h4 className="font-medium text-gray-800" data-id="element-1964">
                        Send via email
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1965">
                        Send the payment link to their email address
                      </p>
                    </div>
                  </div>
                  {selectedOption === 'email' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1966">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1967">
                        Email address
                      </label>
                      <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="name@example.com" value={recipient} onChange={e => setRecipient(e.target.value)} data-id="element-1968" />
                    </div>}
                </div>
                {/* SMS option */}
                <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedOption === 'sms' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedOption('sms')} data-id="element-1969">
                  <div className="flex items-center" data-id="element-1970">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-1971">
                      <SmartphoneIcon size={20} className="text-green-600" data-id="element-1972" />
                    </div>
                    <div className="ml-4 flex-1" data-id="element-1973">
                      <h4 className="font-medium text-gray-800" data-id="element-1974">
                        Send via text message
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1975">
                        Send the payment link to their phone
                      </p>
                    </div>
                  </div>
                  {selectedOption === 'sms' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1976">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1977">
                        Phone number
                      </label>
                      <input type="tel" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="+1 (555) 123-4567" value={recipient} onChange={e => setRecipient(e.target.value)} data-id="element-1978" />
                    </div>}
                </div>
                {/* QR Code option */}
                <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedOption === 'qrcode' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedOption('qrcode')} data-id="element-1979">
                  <div className="flex items-center" data-id="element-1980">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-1981">
                      <QrCodeIcon size={20} className="text-amber-600" data-id="element-1982" />
                    </div>
                    <div className="ml-4 flex-1" data-id="element-1983">
                      <h4 className="font-medium text-gray-800" data-id="element-1984">
                        Generate QR code
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1985">
                        Create a scannable QR code for the payment link
                      </p>
                    </div>
                  </div>
                  {selectedOption === 'qrcode' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1986">
                      <div className="flex flex-col items-center" data-id="element-1987">
                        <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3" data-id="element-1988">
                          <QRCodeSVG value="https://payment.example.com/pay/abc123" size={200} level="H" includeMargin={true} data-id="element-1989" />
                        </div>
                        <button className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors flex items-center" onClick={handleDownloadQR} data-id="element-1990">
                          {qrDownloaded ? <>
                              <CheckIcon size={16} className="mr-2" data-id="element-1991" />
                              Downloaded
                            </> : <>
                              <DownloadIcon size={16} className="mr-2" data-id="element-1992" />
                              Download QR Code
                            </>}
                        </button>
                        <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-1993">
                          Customers can scan this QR code with their phone's
                          camera to open the payment link
                        </p>
                      </div>
                    </div>}
                </div>
                {/* Copy link option */}
                <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedOption === 'copy' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedOption('copy')} data-id="element-1994">
                  <div className="flex items-center" data-id="element-1995">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-1996">
                      <LinkIcon size={20} className="text-purple-600" data-id="element-1997" />
                    </div>
                    <div className="ml-4 flex-1" data-id="element-1998">
                      <h4 className="font-medium text-gray-800" data-id="element-1999">
                        Copy link to share
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-2000">
                        Copy the payment link to share yourself
                      </p>
                    </div>
                  </div>
                  {selectedOption === 'copy' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-2001">
                      <div className="flex" data-id="element-2002">
                        <input type="text" className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500 bg-gray-50" value="https://payment.example.com/pay/abc123" readOnly data-id="element-2003" />
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 transition-colors flex items-center" onClick={() => handleCopyLink()} data-id="element-2004">
                          {copied ? <>
                              <CheckIcon size={16} className="mr-1" data-id="element-2005" />
                              Copied
                            </> : <>
                              <LinkIcon size={16} className="mr-1" data-id="element-2006" />
                              Copy
                            </>}
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </>}
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-2007">
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md mr-3 hover:bg-gray-50 transition-colors" onClick={onClose} data-id="element-2008">
            Cancel
          </button>
          {hasMultipleRecipients ? <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" onClick={handleSend} data-id="element-2009">
              <SendIcon size={16} className="mr-2" data-id="element-2010" />
              Send to all recipients
            </button> : <>
              {(selectedOption === 'email' || selectedOption === 'sms') && <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" onClick={handleSend} disabled={!recipient} data-id="element-2011">
                  <SendIcon size={16} className="mr-2" data-id="element-2012" />
                  Send
                </button>}
              {(selectedOption === 'copy' || selectedOption === 'qrcode') && <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={onClose} data-id="element-2013">
                  Done
                </button>}
            </>}
        </div>
      </div>
    </div>;
}